// DEPENDENCIES
import { FormikValues } from "formik";
import {
  doc,
  DocumentData,
  DocumentReference,
  Firestore,
} from "@firebase/firestore";

// TYPES
import { PitbullCountry, Step } from "../../types";

// UTILS
import {
  dniRequired,
  radioOrSingleSelectRequired,
  tramitNumberRequired,
} from "../../validations";
import { getParsedRenaperData, onContinueRenaper } from "../../app/utils";
import {
  hasRenaperData,
  checkIsEmailAvailable,
  genderOptions,
  getDniFromCuit,
  isStepOneSent,
} from "../../utils";
import { documentTypesParsed, firestoreCollections } from "../../constants";
import { onUpdateFormData } from "../../reducers/onboardingDataReducer";
import { sendIndividualDocument } from "../../connectors/connectors";

// COMPONENTS
import LivenessValidationCard from "../../components/LivenessValidationCard/LivenessValidationCard";
import { hideLivenessStepHeader } from "../../components/LivenessValidationCard/utils/utils";
import UploadDniCard from "../../components/UploadDniCard/UploadDniCard";

import EditIcon from "../../assets/images/icons/ui/edit.svg?react";
import { ReactNode } from "react";
import { AppDispatch, RootState } from "../../app/store";

type FirestoreRefFn = ({
  db,
  uuid,
  email,
}: {
  db: Firestore;
  uuid: string;
  email: string;
}) => DocumentReference<DocumentData, DocumentData>;

type OnContinueUploadDni = (
  values: FormikValues,
  dispatch: AppDispatch,
  state: RootState,
) => Promise<void | {
  nextStep?: number;
  nextSubStep?: number;
  newValuesToForm?: FormikValues;
}>;

interface IdentityStepOptions {
  getDniRef?: FirestoreRefFn;
  getLivenessRef?: FirestoreRefFn;
  onContinueUploadDni?: OnContinueUploadDni;
  uploadDniSubtitle?: string | ReactNode;
  uploadDniQrDescription?: ReactNode;
  onCloseUploadDni?: () => void;
  uploadDniCloseText?: string;
  onCustomScanDni?: (
    front: { name: string; base64: string },
    back: { name: string; base64: string },
    uuid: string,
    dniRef: DocumentReference<DocumentData, DocumentData>,
  ) => Promise<void>;
  onUploadDniFallback?: (
    front: { name: string; base64: string },
    back: { name: string; base64: string },
    uuid: string,
  ) => Promise<void>;
  onCustomScanError?: (error: unknown) => void;
  onUploadDniFallbackError?: (error: unknown) => void;
  stepUploadModal?: ReactNode;
  editableIdentityDocument?: boolean;
  hideEditDni?: boolean;
  onContinueDniManualForm?: (values: FormikValues) => Promise<void>;
  onCustomGetSessionId?: (
    uuid: string,
  ) => Promise<{ data: { sessionId: string } }>;
  onCustomValidateFace?: (
    uuid: string,
    sessionId: string,
    livenessRef: DocumentReference<DocumentData, DocumentData>,
  ) => Promise<void>;
  onCustomValidateFaceError?: (
    error: unknown,
    reachedAttemptsLimit: boolean,
  ) => void;
  livenessDescription?: ReactNode;
  onCloseLiveness?: () => void;
  stepLivenessModal?: ReactNode;
}

const IdentityStep = (
  countries: PitbullCountry[],
  options?: IdentityStepOptions,
): Omit<Step, "step"> => {
  const defaultGetDniRef: FirestoreRefFn = ({ db, uuid }) =>
    doc(db, firestoreCollections.dni, uuid);

  const defaultGetLivenessRef: FirestoreRefFn = ({ db, uuid }) =>
    doc(db, firestoreCollections.liveness, uuid);

  const resolvedGetDniRef = options?.getDniRef ?? defaultGetDniRef;
  const resolvedGetLivenessRef =
    options?.getLivenessRef ?? defaultGetLivenessRef;

  const substeps: Omit<Step, "step">[] = [
    {
      titleOfStep: "Vamos a verificar tu DNI",
      label: "uploadDNI", // Used to identify the step in remoteSteps
      component: (dispatch, state, formik) => (
        <UploadDniCard
          dispatch={dispatch}
          state={state}
          formik={formik}
          getDniRef={resolvedGetDniRef}
          onUploadDni={async (type, file, uuid, cuit) => {
            await sendIndividualDocument({
              draftUUID: uuid,
              documentType: documentTypesParsed[type],
              file,
              cuit: cuit,
            });
          }}
          onCustomScanDni={options?.onCustomScanDni}
          onUploadDniFallback={options?.onUploadDniFallback}
          onCustomScanError={options?.onCustomScanError}
          onUploadDniFallbackError={options?.onUploadDniFallbackError}
          qrDescription={options?.uploadDniQrDescription}
          onClose={options?.onCloseUploadDni}
          closeText={options?.uploadDniCloseText}
        />
      ),
      onContinue: async (values, dispatch, state) => {
        if (!state || !dispatch) return;

        const hasScanResult = hasRenaperData(state);
        const { completedRemote } = state.onboardingData;

        // A custom continue (Allaria Digital) has no RENAPER data in the store:
        // its backend validated the DNI, so a finished scan (from the phone or
        // the camera) is enough. "Subir imágenes" also continues from here, and
        // has to reach the photos.
        if (
          options?.onContinueUploadDni &&
          (hasScanResult || completedRemote)
        ) {
          return await options?.onContinueUploadDni(values, dispatch, state);
        }

        if (!completedRemote) return;
        if (!hasScanResult) return;

        const informationParsed = state.renaper;

        await checkIsEmailAvailable({
          dispatch,
          idNumber: values.identityDocument || getDniFromCuit(values.cuit),
        });

        const body = getParsedRenaperData(
          values,
          state,
          informationParsed,
          countries,
        );
        dispatch(onUpdateFormData({ ...body }));

        return {
          newValuesToForm: body,
        };
      },
      // The scan with the camera is validated here, the photos on the back one.
      stepModals: options?.stepUploadModal,
      insideForm: true,
    },
    {
      titleOfStep: "Cargá fotos del frente de tu DNI",
      component: (dispatch, state, formik) => (
        <UploadDniCard
          dispatch={dispatch}
          state={state}
          formik={formik}
          currentDniStep="upload_front"
          getDniRef={resolvedGetDniRef}
          onUploadDni={async (type, file, uuid, cuit) => {
            await sendIndividualDocument({
              draftUUID: uuid,
              documentType: documentTypesParsed[type],
              file,
              cuit: cuit,
            });
          }}
          onCustomScanDni={options?.onCustomScanDni}
          onUploadDniFallback={options?.onUploadDniFallback}
          onCustomScanError={options?.onCustomScanError}
          onUploadDniFallbackError={options?.onUploadDniFallbackError}
        />
      ),
      skipWhen: (_, state) => state.onboardingData.completedRemote,
      stepModals: options?.stepUploadModal,
      insideForm: true,
    },
    {
      titleOfStep: "Cargá fotos del dorso de tu DNI",
      component: (dispatch, state, formik) => (
        <UploadDniCard
          dispatch={dispatch}
          state={state}
          formik={formik}
          currentDniStep="upload_back"
          getDniRef={resolvedGetDniRef}
          onUploadDni={async (type, file, uuid, cuit) => {
            await sendIndividualDocument({
              draftUUID: uuid,
              documentType: documentTypesParsed[type],
              file,
              cuit: cuit,
            });
          }}
          onCustomScanDni={options?.onCustomScanDni}
          onUploadDniFallback={options?.onUploadDniFallback}
          onCustomScanError={options?.onCustomScanError}
          onUploadDniFallbackError={options?.onUploadDniFallbackError}
        />
      ),
      skipWhen: (_, state) => state.onboardingData.completedRemote,
      onContinue: async (values, dispatch, state) => {
        if (!state || !dispatch) return;

        if (options?.onContinueUploadDni) {
          return await options?.onContinueUploadDni(values, dispatch, state);
        }

        if (!hasRenaperData(state)) return;

        const informationParsed = state.renaper;

        await checkIsEmailAvailable({
          dispatch,
          idNumber: values.identityDocument || getDniFromCuit(values.cuit),
        });

        const body = getParsedRenaperData(
          values,
          state,
          informationParsed,
          countries,
        );
        dispatch(onUpdateFormData({ ...body }));

        return {
          newValuesToForm: body,
        };
      },
      stepModals: options?.stepUploadModal,
      insideForm: true,
    },
    {
      titleOfStep: "Información de tu DNI",
      fields: [
        {
          placeholder: "XX.XXX.XXX",
          initialValue: "",
          type: "dni",
          name: "identityDocument",
          label: "DNI",
          validation: dniRequired,
          disabled: !options?.editableIdentityDocument,
          ...(options?.editableIdentityDocument || options?.hideEditDni
            ? {}
            : {
                tooltip: {
                  label: <EditIcon style={{ color: "var(--icon-emphasis)" }} />,
                  modal: "modal-edit-dni",
                  external: true,
                },
              }),
          columns: 2,
        },
        {
          placeholder: "XXXXXXXXXXX",
          initialValue: "",
          type: "text",
          name: "tramitNumber",
          label: "Número de trámite",
          tooltip: {
            label: "¿Dónde lo encuentro?",
            modal: "modal-tramit-number",
          },
          validation: tramitNumberRequired,
          infoText:
            "Debe contener 11 dígitos. Si es necesario, agregá ceros al comienzo",
          inputMode: "numeric",
          maxLength: 11,
          columns: 2,
        },
        {
          name: "gender",
          type: "select",
          placeholder: "Seleccioná una opción",
          validation: radioOrSingleSelectRequired(genderOptions),
          initialValue: "",
          options: genderOptions,
          label: "Género",
        },
      ],
      onContinue: async (values: FormikValues, dispatch, state) => {
        if (!dispatch || !values || !state) return;
        if (options?.onContinueDniManualForm) {
          await options.onContinueDniManualForm(values);
          return;
        }
        return await onContinueRenaper(values, dispatch, state, countries);
      },
      skipWhen: (_, state) => hasRenaperData(state),
      disableBackNavigation: true,
      disableButtonBack: true,
    },
    {
      label: "livenessValidation", // Used to identify the step in remoteSteps
      titleOfStep: "Validá tu identidad",
      hideFormHeader: hideLivenessStepHeader,
      component: (dispatch, state, formik) => (
        <LivenessValidationCard
          dispatch={dispatch}
          state={state}
          formik={formik}
          getLivenessRef={resolvedGetLivenessRef}
          onCustomGetSessionId={options?.onCustomGetSessionId}
          onCustomValidateFace={options?.onCustomValidateFace}
          onCustomValidateFaceError={options?.onCustomValidateFaceError}
          description={options?.livenessDescription}
          onClose={options?.onCloseLiveness}
        />
      ),
      stepModals: options?.stepLivenessModal,
      insideForm: true,
    },
  ];

  return {
    label: "identity",
    name: "Identidad",
    skipWhen: (_, state) => isStepOneSent(state),
    disableBackNavigation: true,
    disableButtonBack: true,
    hideFormHeaderOnMobile: true,
    subSteps: substeps.map((substep, i) => ({
      step: i + 1,
      ...substep,
    })),
  };
};

export default IdentityStep;
